import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, PenTool, Workflow, X } from 'lucide-react';
import CanvasList from './CanvasList';
import { DiagramList } from './DiagramList';

type VisualTab = 'canvases' | 'diagrams';

export function Visuales() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<VisualTab>('canvases');
  const [chooserOpen, setChooserOpen] = useState(false);

  const openNewCanvas = () => navigate('/canvas/new');
  const openNewDiagram = () => navigate('/diagrams/new');

  return (
    <div className="page-enter max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Visuales</h1>
          <p className="text-sm text-text-muted mt-1">Pizarrones y diagramas</p>
        </div>
        <button
          onClick={() => setChooserOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-xd-action px-4 py-2 text-sm font-semibold text-[var(--xds-fg)] hover:bg-xd-action-hover transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        <button
          onClick={() => setTab('canvases')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'canvases'
              ? 'border-xd-action text-xd-action'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          Pizarras
        </button>
        <button
          onClick={() => setTab('diagrams')}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'diagrams'
              ? 'border-xd-action text-xd-action'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          Diagramas
        </button>
      </div>

      {/* Tab panels */}
      {tab === 'canvases' ? (
        <CanvasList embedded onNew={openNewCanvas} />
      ) : (
        <DiagramList embedded onNew={openNewDiagram} />
      )}

      {/* Create chooser */}
      {chooserOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setChooserOpen(false)}
        >
          <div
            className="xd-overlay w-full max-w-md border border-border bg-surface p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-text">Crear visual</h2>
              <button
                onClick={() => setChooserOpen(false)}
                className="text-text-subtle hover:text-text-muted"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-text-muted mb-5">Elegí con qué queres trabajar</p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={openNewCanvas}
                className="card-hover group rounded-xl border border-border bg-surface-2 p-4 text-left hover:border-xd-action/50 transition-colors"
              >
                <div className="h-20 rounded-lg bg-gradient-to-br from-xd-info/10 to-xd-badge/10 flex items-center justify-center group-hover:from-xd-info/20 group-hover:to-xd-badge/20 transition-colors">
                  <PenTool className="h-8 w-8 text-xd-info/60 group-hover:text-xd-info transition-colors" />
                </div>
                <h3 className="mt-3 font-semibold text-text text-sm">Pizarra</h3>
                <p className="text-xs text-text-muted mt-1 leading-relaxed">
                  Libre, a mano. Ideas y bocetos rápidos.
                </p>
              </button>

              <button
                onClick={openNewDiagram}
                className="card-hover group rounded-xl border border-border bg-surface-2 p-4 text-left hover:border-xd-action/50 transition-colors"
              >
                <div className="h-20 rounded-lg bg-gradient-to-br from-xd-badge/10 to-xd-info/10 flex items-center justify-center group-hover:from-xd-badge/20 group-hover:to-xd-info/20 transition-colors">
                  <Workflow className="h-8 w-8 text-xd-badge/60 group-hover:text-xd-badge transition-colors" />
                </div>
                <h3 className="mt-3 font-semibold text-text text-sm">Diagrama</h3>
                <p className="text-xs text-text-muted mt-1 leading-relaxed">
                  Estructurado. AWS, UML y BPMN (draw.io).
                </p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
